import { useRef, useState } from 'react';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import {Box, Button, Card, TextField } from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { useErrorBoundary } from 'react-error-boundary';

const QuestionForm = (): JSX.Element => {

    const { showBoundary } = useErrorBoundary(); 
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const formRef = useRef<HTMLFormElement>(null);

    const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
    const [securityToken, setSecurityToken] = useState('');
    const [isFullNameError, setIsFullNameError] = useState(false);
    const [isEmailError, setIsEmailError] = useState(false);
    const [isMessageError, setIsMessageError] = useState(false);

    //testing ONLY for local dev, provided by Google
    let siteKey = "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI";

    //staging
    //need to replace it with production key before launching
    if(window.location.href.includes("ocpf.us")) {
        siteKey = process.env.REACT_APP_PRODUCTION_RECAPTCHA_SITE_KEY || "";
    }

    const handleCaptchaChange = (token: string | null) => {
        setIsCaptchaVerified(!!token);
        setSecurityToken(token || '');
      };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const data = {
            fullName: formData.get('fullName'),
            emailAddress: formData.get('emailAddress'),
            message: formData.get('message'),
            recaptchaToken: securityToken,
        }
        // Validate form fields
        setIsFullNameError(!data.fullName);
        setIsEmailError(!data.emailAddress);
        setIsMessageError(!data.message);


        // Check if CAPTCHA is verified
        if(!data.fullName || !data.emailAddress || !data.message) {
            recaptchaRef.current?.reset();
            setIsCaptchaVerified(false);
            return;
        }
        else{
            if (isCaptchaVerified) {
                submitForm(data);
                //submitMessage(data);
              } 
            else {
                alert('Please verify the CAPTCHA before submitting.');
              }
        }
        
    }

    //to-be-removed
    const submitMessage = async (data: any) => {
        
        try{

            let params = new URLSearchParams({
                emailAddress: data.emailAddress,
                fullName: data.fullName,
                message: data.message,
                recaptchaToken: data.recaptchaToken
            });

            await fetch(`${process.env.REACT_APP_API_BASE_URL}/submitMessaqe?${params.toString()}`)
            .then(result => {
              if (!result.ok) {
                showBoundary(new Error('Failed to submit the form.'));
              }
              return result.json();
            })
            .then(result => {
                recaptchaRef.current?.reset();
                setIsCaptchaVerified(false);
                alert('Form submitted successfully!');
            });
    
        }catch(error){
          console.error('Uncaught Error:', error);
          showBoundary(new Error(`${error}`));
        }
      }

    const submitForm = async (data: any) => {

            let params = new URLSearchParams({
                emailAddress: data.emailAddress,
                fullName: data.fullName,
                message: data.message,
                recaptchaToken: data.recaptchaToken
            });

        try {
            const response = await fetch(`https://api.ocpf.us/ocpfusdataapi/submitMessage/submitMessage`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data)
            });
            const responseData = await response.json();
            if (!responseData.success) {
                showBoundary(new Error('Failed to submit the form.'));
                alert('Failed to submit the form.');
            }
            else{
                recaptchaRef.current?.reset();
                setIsCaptchaVerified(false);
                alert('Form submitted successfully!');
                if (formRef.current) {
                    formRef.current.reset();
                }
            }
            
        } catch (error) {
            console.error('Uncaught Error:', error);
        }
    }

    return (
    <Box marginTop={3} marginBottom={4}>
        <Box marginBottom={4}>
            <Typography
                variant="h6"
                fontWeight={550}
                gutterBottom
                align={'center'}
            >
            Submit a Question to OCPF
            </Typography>
            <Typography
                variant="h6"
                color={'text.secondary'}
                align={'center'}
                data-aos={'fade-up'}
            >
                Have a question, or just need help about campaign finance? You can send OCPF a message via the form below.
            </Typography>
        </Box>
        <Box bgcolor="alternate.main" display={'flex'} justifyContent={'center'}>
            <Box
                padding={{ xs: 3, sm: 6 }}
                width={1}
                component={Card}
                boxShadow={1}
                marginTop={3}
                marginBottom={4}
                maxWidth={600}
            >
                <form onSubmit={handleSubmit} noValidate autoComplete="off" ref={formRef}>
                    <Grid container spacing={4}>
                        <Grid size={12}>
                            <TextField
                                required
                                error={isFullNameError}
                                helperText={isFullNameError ? "Please enter your full name!" : ""}
                                label="Full name"
                                variant="outlined"
                                color="primary"
                                size="medium"
                                name="fullName"
                                fullWidth
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                required
                                error={isEmailError}
                                helperText={isEmailError ? "Please enter your email address!" : ""}
                                label="Email"
                                type="email"
                                variant="outlined"
                                color="primary"
                                size="medium"
                                name="emailAddress"
                                fullWidth
                             />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                required
                                error={isMessageError}
                                helperText={isMessageError ? "Please enter a message to OCPF!" : ""}
                                label="Message"
                                multiline
                                rows={6}
                                variant="outlined"
                                color="primary"
                                size="medium"
                                name="message"
                                fullWidth
                            />
                        </Grid>
                        <Grid size={12} display={'flex'} justifyContent={'center'}>
                            <ReCAPTCHA 
                                sitekey={siteKey} 
                                ref={recaptchaRef} 
                                onChange={handleCaptchaChange}
                                onExpired={() => setIsCaptchaVerified(false)}
                                />
                        </Grid>
                        <Grid container justifyContent={'center'} size={12}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="medium"
                                type="submit"
                                fullWidth
                            >
                            Submit
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Box>
        </Box>
    </Box>
    )
  
};

export default QuestionForm;